import React, { useEffect, useRef, useState } from "react"
import { View, StyleSheet, Text, Animated, Easing } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { theme } from "@common/theme/theme"

/** En deçà, la fermeture devient une information utile. Au-delà, du décor. */
const VISIBLE_WITHIN_MS = 24 * 3600 * 1000

const remainingFrom = (closesAt) => {
    if (!closesAt) return null
    const ms = new Date(closesAt).getTime() - Date.now()
    return Number.isFinite(ms) ? ms : null
}

/**
 * Le compte à rebours de fermeture d'un week-end.
 *
 * Il n'apparaît que dans les **dernières 24 heures** : affiché en permanence il
 * devient un ornement qu'on cesse de lire, et c'est seulement à la fin qu'il
 * dit quelque chose de vrai.
 *
 * L'instant de fermeture vient du backend (`closes_at`), calculé sur la règle
 * même qui refuse la vente — le front ne redérive jamais le délai, il
 * l'afficherait forcément à côté.
 */
const StageClosingCountdown = ({ closesAt }) => {
    const [remaining, setRemaining] = useState(() => remainingFrom(closesAt))
    const pulse = useRef(new Animated.Value(0)).current

    useEffect(() => {
        setRemaining(remainingFrom(closesAt))
        if (!closesAt) return undefined
        const tick = setInterval(
            () => setRemaining(remainingFrom(closesAt)),
            30000
        )
        return () => clearInterval(tick)
    }, [closesAt])

    const visible =
        remaining !== null && remaining > 0 && remaining <= VISIBLE_WITHIN_MS

    useEffect(() => {
        if (!visible) return undefined
        // Battement lent : il attire l'œil une fois, pas en continu.
        const loop = Animated.loop(
            Animated.sequence([
                Animated.timing(pulse, {
                    toValue: 1,
                    duration: 1100,
                    easing: Easing.inOut(Easing.quad),
                    useNativeDriver: true,
                }),
                Animated.timing(pulse, {
                    toValue: 0,
                    duration: 1100,
                    easing: Easing.inOut(Easing.quad),
                    useNativeDriver: true,
                }),
            ])
        )
        loop.start()
        return () => loop.stop()
    }, [visible])

    if (!visible) return null

    const hours = Math.floor(remaining / 3600000)
    const minutes = Math.floor((remaining % 3600000) / 60000)
    const label =
        hours > 0
            ? `${hours} h ${String(minutes).padStart(2, "0")}`
            : `${minutes} min`

    return (
        <View style={styles.wrap}>
            <Animated.View
                style={{
                    opacity: pulse.interpolate({
                        inputRange: [0, 1],
                        outputRange: [1, 0.45],
                    }),
                }}
            >
                <FontAwesome5
                    name="clock"
                    size={11}
                    color={theme.colors.warning}
                />
            </Animated.View>
            <Text style={styles.text}>
                Il reste <Text style={styles.strong}>{label}</Text> pour
                réserver ce week-end
            </Text>
        </View>
    )
}

const styles = StyleSheet.create({
    wrap: {
        flexDirection: "row",
        alignItems: "center",
        gap: 7,
        alignSelf: "flex-start",
        marginTop: 10,
        paddingVertical: 5,
        paddingHorizontal: 10,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: theme.colors.warning,
        backgroundColor: theme.colors.warningLighter,
    },
    text: {
        fontFamily: "Montserrat-Regular",
        fontSize: 11.5,
        color: theme.colors.warning,
    },
    strong: { fontFamily: "Montserrat-Bold" },
})

export default StageClosingCountdown
